import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Download, Trash2, UserPlus } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useOutletContext } from "react-router";
import { api } from "../../api/client";
import { useToast } from "../../components/Toast";
import { Alert, ICON, TextField } from "../../components/ui";
import type { ShellContext } from "../AppShell";
import { ConfirmDialog } from "../menus/dialogs";

// Setări → Echipă and Setări → Date și cont (LOT 5). The owner manages the team and the restaurant's data; the
// server checks every action again (only the owner).

interface Member {
  userId: string;
  name: string;
  email: string;
  role: string;
  since: number;
  pending: boolean;
}

const ROLE: Record<string, string> = { owner: "Proprietar", manager: "Manager", staff: "Personal" };

export function TeamTab() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const isOwner = membership.role === "owner";
  const qc = useQueryClient();
  const toast = useToast();
  const team = useQuery({
    queryKey: ["team", rid],
    queryFn: () => api<{ members: Member[] }>("GET", `/restaurants/${rid}/team`),
  });
  const [form, setForm] = useState({ email: "", name: "", role: "manager" });
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<{ userId: string; name: string } | null>(null);
  const [changing, setChanging] = useState<{ userId: string; name: string; role: string } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["team", rid] });

  async function add(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setLink(null);
    try {
      const r = await api<{ link: string | null; emailed: boolean; expiresAt: number | null }>(
        "POST",
        `/restaurants/${rid}/team`,
        form,
      );
      setLink(r.link);
      toast(
        r.link
          ? "Invitație creată. E-mailul nu a putut fi trimis: trimite tu linkul persoanei."
          : r.expiresAt
            ? `Invitația a fost trimisă pe e-mail la ${form.email}.`
            : `${form.name || "Persoana"} a primit acces și a fost anunțat pe e-mail.`,
      );
      setForm({ email: "", name: "", role: "manager" });
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err));
    } finally {
      await refresh();
      setBusy(false);
    }
  }
  async function act(fn: () => Promise<unknown>, done: string) {
    try {
      await fn();
      toast(done);
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err));
    } finally {
      await refresh();
    }
  }

  return (
    <>
      <section className="card stack" style={{ gap: 12 }} aria-labelledby="team-h">
        <h2 id="team-h" className="h2">
          Echipa restaurantului
        </h2>
        <span className="muted" style={{ fontSize: 14 }}>
          Managerii pot modifica meniurile și publica; personalul vede meniurile și schimbă doar
          disponibilitatea produselor. Doar proprietarul gestionează echipa.
        </span>
        {team.isPending && (
          <p className="muted" role="status" style={{ margin: 0 }}>
            Se încarcă echipa…
          </p>
        )}
        {team.isError && (
          <Alert tone="error" live>
            Echipa nu a putut fi încărcată. Reîncarcă pagina.
          </Alert>
        )}
        <ul className="verify-list">
          {(team.data?.members ?? []).map((m) => (
            <li key={m.userId} className="verify-row">
              <div className="stack" style={{ gap: 2, minWidth: 0 }}>
                <strong style={{ fontWeight: 600 }}>{m.name}</strong>
                <span className="caption muted" style={{ overflowWrap: "anywhere" }}>
                  {m.email} · {ROLE[m.role] ?? m.role}
                  {m.pending ? " · invitație în așteptare" : ""}
                </span>
              </div>
              {isOwner && m.role !== "owner" && (
                <div className="row" style={{ gap: 8 }}>
                  <label className="sr-only" htmlFor={`role-${m.userId}`}>{`Rolul lui ${m.name}`}</label>
                  <select
                    id={`role-${m.userId}`}
                    className="field-input"
                    style={{ height: 40 }}
                    value={m.role}
                    onChange={(e) => setChanging({ userId: m.userId, name: m.name, role: e.target.value })}
                  >
                    <option value="manager">Manager</option>
                    <option value="staff">Personal</option>
                  </select>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setRemoving({ userId: m.userId, name: m.name })}
                  >
                    <Trash2 {...ICON} aria-hidden="true" />
                    Elimină
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>
      {changing && (
        <ConfirmDialog
          title={`Schimbi rolul lui ${changing.name}?`}
          body={
            changing.role === "manager"
              ? `${changing.name} va putea modifica meniurile și publica.`
              : `${changing.name} va vedea meniurile și va schimba doar disponibilitatea produselor.`
          }
          confirmLabel={changing.role === "manager" ? "Fă-l manager" : "Fă-l personal"}
          danger={false}
          onClose={() => setChanging(null)}
          onConfirm={async () => {
            const who = changing;
            setChanging(null);
            await act(
              () => api("PATCH", `/restaurants/${rid}/team/${who.userId}`, { role: who.role }),
              "Rol schimbat.",
            );
          }}
        />
      )}
      {removing && (
        <ConfirmDialog
          title={`Elimini pe ${removing.name}?`}
          body={`${removing.name} nu va mai putea intra în contul restaurantului. Îl poți invita din nou oricând.`}
          confirmLabel="Elimină"
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            const who = removing;
            setRemoving(null);
            await act(
              () => api("DELETE", `/restaurants/${rid}/team/${who.userId}`),
              `${who.name} nu mai are acces.`,
            );
          }}
        />
      )}
      {isOwner && (
        <section className="card stack" style={{ gap: 12 }} aria-labelledby="team-add-h">
          <h2 id="team-add-h" className="h2">
            Adaugă o persoană
          </h2>
          <form className="stack" style={{ gap: 12, maxWidth: 420 }} onSubmit={add}>
            <TextField
              id="team-name"
              label="Nume"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              id="team-email"
              label="E-mail"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <label className="stack" style={{ gap: 4 }}>
              <span className="field-label">Rol</span>
              <select
                className="field-input"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <option value="manager">Manager</option>
                <option value="staff">Personal</option>
              </select>
            </label>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ alignSelf: "flex-start" }}
              disabled={busy}
            >
              <UserPlus {...ICON} aria-hidden="true" />
              Adaugă
            </button>
          </form>
          {link && (
            <Alert
              tone="warning"
              title="E-mailul nu a plecat: trimite tu linkul (valabil 3 zile, o singură dată)"
              live
            >
              <span style={{ overflowWrap: "anywhere" }}>{link}</span>{" "}
              <button
                type="button"
                className="link-btn"
                onClick={() => void navigator.clipboard?.writeText(link)}
              >
                <Copy size={14} aria-hidden="true" /> Copiază
              </button>
              <br />
              Trimite-l persoanei: cu el își alege parola. Nu îl vei mai vedea.
            </Alert>
          )}
        </section>
      )}
    </>
  );
}

export function DataTab() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const isOwner = membership.role === "owner";
  const toast = useToast();
  const [confirm, setConfirm] = useState("");
  const [sent, setSent] = useState(false);
  if (!isOwner) {
    return (
      <section className="card">
        <p style={{ margin: 0 }}>Doar proprietarul restaurantului exportă sau șterge datele.</p>
      </section>
    );
  }
  async function requestDeletion(e: FormEvent) {
    e.preventDefault();
    try {
      await api("POST", `/restaurants/${rid}/data/deletion-request`, { confirmName: confirm });
      setSent(true);
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err));
    }
  }
  return (
    <>
      <section className="card stack" style={{ gap: 12 }} aria-labelledby="exp-h">
        <h2 id="exp-h" className="h2">
          Exportă datele
        </h2>
        <span className="muted" style={{ fontSize: 14 }}>
          Un fișier JSON cu restaurantul, meniurile, produsele, traducerile, versiunile publicate, echipa și
          istoricul recent al modificărilor.
        </span>
        <a
          className="btn btn-secondary"
          style={{ alignSelf: "flex-start" }}
          href={`/api/restaurants/${rid}/data/export`}
        >
          <Download {...ICON} aria-hidden="true" />
          Descarcă exportul
        </a>
      </section>
      <section className="card stack" style={{ gap: 12 }} aria-labelledby="del-h">
        <h2 id="del-h" className="h2">
          Șterge contul și datele
        </h2>
        {sent ? (
          <Alert tone="success" live>
            Cererea a fost înregistrată. Echipa Meniova șterge datele în cel mult 30 de zile și te anunță.
          </Alert>
        ) : (
          <form className="stack" style={{ gap: 12, maxWidth: 420 }} onSubmit={requestDeletion}>
            <span className="muted" style={{ fontSize: 14 }}>
              Meniul public se oprește și datele restaurantului se șterg definitiv. Descarcă exportul înainte.
              Ca să confirmi, scrie numele restaurantului: <strong>{membership.restaurant.name}</strong>.
            </span>
            <TextField
              id="del-confirm"
              label="Numele restaurantului"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            <button
              type="submit"
              className="btn btn-danger"
              style={{ alignSelf: "flex-start" }}
              disabled={!confirm.trim()}
            >
              Trimite cererea de ștergere
            </button>
          </form>
        )}
      </section>
    </>
  );
}
